import {
  NoiseReduction,
  TranscriptionLanguageWhisperAvailable,
  TranscriptionProviderName,
} from "@/hooks/transcriber/types";
import { Combobox } from "@/components/ui/Combobox";
import { Label } from "@/components/ui/label";
import { TooltipHelp } from "@/components/ui/TooltipHelp";
import { t } from "i18next";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/Toggle/toggle-group";
import { useEffect, useState } from "react";
import { User, Users, ChevronDown, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Switch } from "@/components/ui/switch";
import { useAudioInputDevices } from "@/hooks/useAudioInputDevices";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "../select";
import { AppConfig } from "@/common/appConfig";
import { useMobileDevicesStore } from "@/store/mobileDevices.store";
import { useAuthStore } from "@/store/auth.store";
import { useAppSettingsStore } from "@/store/appSettings.store";
import { TranscriptionProvidersSelector } from "./TranscriptionProviderSelector";
import { useSelectTranscriptionProvider } from "@/hooks/visits/useSelectTranscriptionProvider";

export const AdvancedSettings = ({
  advancedSettingsEnabled = true,
  mobileDevicesEnabled = true,
}: {
  advancedSettingsEnabled?: boolean;
  mobileDevicesEnabled?: boolean;
}): JSX.Element => {
  const {
    inputLanguage,
    noiseReduction,
    saveInputLanguage,
    saveNoiseReduction,
    isRealtimeEnabled,
    noiseSuppression,
    saveNoiseSuppression,
    saveMicDevice,
    micDevice,
    saveTranscriptionProvider,
  } = useRecordingSettingsStore();
  const [open, setOpen] = useState(false);
  const { mics, loading: loadingMics } = useAudioInputDevices();
  const { userData } = useAuthStore.getState();
  const { setInputLanguage: saveInputLanguageAppSettings } =
    useAppSettingsStore.getState();

  const {
    getMobileDevices,
    loading: loadingDevices,
    fetch: fetchDevices,
  } = useMobileDevicesStore();
  const isProductionEnv = import.meta.env.VITE_APP_ENVIRONMENT === "prod";

  useEffect(() => {
    if (userData != null) {
      void fetchDevices({ userId: userData.id });
    }
  }, [fetchDevices, userData]);

  useEffect(() => {
    const defaultMic = mics.find((m) => m.default);
    if (defaultMic) {
      saveMicDevice(defaultMic);
    }
  }, [mics, saveMicDevice]);

  useEffect(() => {
    if (!isRealtimeEnabled && (!inputLanguage || inputLanguage === "auto")) {
      saveInputLanguage("eng");
      saveInputLanguageAppSettings("eng");
    }
  }, [
    isRealtimeEnabled,
    inputLanguage,
    saveInputLanguage,
    saveInputLanguageAppSettings,
  ]);

  useEffect(() => {
    if (!isRealtimeEnabled) {
      return;
    }
    const provider =
      inputLanguage === "eng"
        ? TranscriptionProviderName.DEEPGRAM
        : TranscriptionProviderName.OPENAI;
    saveTranscriptionProvider(provider);
  }, [isRealtimeEnabled, inputLanguage, saveTranscriptionProvider]);

  const handleNoiseReductionChange = (value: string): void => {
    const enumValue = value as NoiseReduction;
    if (
      enumValue === NoiseReduction.NEAR_FIELD ||
      enumValue === NoiseReduction.FAR_FIELD
    ) {
      saveNoiseReduction(enumValue);
    } else {
      saveNoiseReduction(NoiseReduction.FAR_FIELD);
    }
  };

  const handleMicDeviceChange = (id: string): void => {
    const devicesToSearch = [...mics];
    if (mobileDevicesEnabled) {
      devicesToSearch.push(...getMobileDevices());
    }
    const device = devicesToSearch.find((d) => d.value === id);
    if (device) {
      saveMicDevice(device);
    }
  };

  const {
    value: transcriptionProviderValue,
    values: transcriptionProviderValues,
    handleChange: transcriptionProviderHandleChange,
  } = useSelectTranscriptionProvider();
  const inputLanguageOptions = isRealtimeEnabled
    ? TranscriptionLanguageWhisperAvailable
    : TranscriptionLanguageWhisperAvailable.filter(
        (language) => language.value !== "auto"
      );
  const nonRealtimeInputLanguage =
    inputLanguage && inputLanguage !== "auto" ? inputLanguage : "eng";
  const inputLanguageValue = isRealtimeEnabled
    ? inputLanguage
    : nonRealtimeInputLanguage;

  useEffect(() => {
    if (!isProductionEnv || !isRealtimeEnabled) {
      return;
    }
    if (inputLanguage === "eng") {
      saveTranscriptionProvider(TranscriptionProviderName.DEEPGRAM);
      return;
    }
    saveTranscriptionProvider(TranscriptionProviderName.OPENAI);
  }, [
    inputLanguage,
    isProductionEnv,
    isRealtimeEnabled,
    saveTranscriptionProvider,
  ]);

  return (
    <div className="space-y-4">
      {/* Input Language */}
      <div className="flex flex-col gap-2">
        <div>
          <Label className="text-tinted-grey-3">
            {t("components.advancedSettings.selectLanguage.label")}
          </Label>
          <div className="flex items-center gap-4">
            <div className="w-full">
              <Combobox
                items={inputLanguageOptions}
                value={inputLanguageValue}
                placeholder={t(
                  "components.advancedSettings.selectLanguage.placeholder"
                )}
                onValueChange={(value) => {
                  saveInputLanguage(value.value);
                  saveInputLanguageAppSettings(value.value);
                }}
              />
            </div>
            <TooltipHelp
              content={t("components.advancedSettings.selectLanguage.tooltip")}
            />
          </div>
        </div>
        <div>
          <Label className="text-tinted-grey-3 text-sm">
            {t("components.advancedSettings.microphoneDevice.label")}
          </Label>
          <div className="flex items-center gap-4">
            <Select
              disabled={loadingMics}
              value={micDevice?.value}
              onValueChange={handleMicDeviceChange}
            >
              <SelectTrigger className="focus:ring-0">
                <SelectValue
                  placeholder={t(
                    "components.advancedSettings.microphoneDevice.placeholder"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {mics.map((mic) => (
                  <SelectItem key={mic.value} value={mic.value}>
                    {mic.label}
                  </SelectItem>
                ))}
                <div hidden={!isRealtimeEnabled}>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel>
                      {t(
                        "components.advancedSettings.microphoneDevice.mobileGroupLabel"
                      )}
                    </SelectLabel>
                    {loadingDevices ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="animate-spin" />
                      </div>
                    ) : (
                      getMobileDevices().map((element, index) => (
                        <SelectItem key={index} value={element.value}>
                          {element.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectGroup>
                </div>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {/* Advanced Settings */}
      <div
        hidden={!AppConfig.enableAdvancedSettings || !advancedSettingsEnabled}
      >
        <div className="flex items-center justify-between">
          <Label className="text-tinted-grey-3 text-sm">
            {t("components.advancedSettings.advancedSettings")}
          </Label>
          <button
            className="text-sm text-primary flex items-center hover:underline transition-colors"
            onClick={() => {
              setOpen((previous) => !previous);
            }}
          >
            {open
              ? t("components.advancedSettings.toggle.hide")
              : t("components.advancedSettings.toggle.show")}
            <ChevronDown
              className={`ml-1 h-4 w-4 transition-transform duration-300 ${
                open ? "rotate-180" : "rotate-0"
              }`}
            />
          </button>
        </div>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="advanced-settings"
              animate={{ height: "auto", opacity: 1 }}
              className="overflow-hidden"
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <div className="space-y-2 mt-2">
                <div className="flex items-center gap-4">
                  <div className="w-full">
                    <Label className="text-tinted-grey-3 text-sm">
                      {t(
                        "components.advancedSettings.optionNoiseReduction.label"
                      )}
                    </Label>
                    <div className="flex items-center gap-4">
                      <ToggleGroup
                        className="w-full"
                        type="single"
                        value={noiseReduction}
                        onValueChange={handleNoiseReductionChange}
                      >
                        <ToggleGroupItem
                          className="flex-1"
                          value={NoiseReduction.FAR_FIELD}
                        >
                          <Users className="mr-2 h-4 w-4" />
                          {t(
                            "components.advancedSettings.optionNoiseReduction.farOption"
                          )}
                        </ToggleGroupItem>
                        <ToggleGroupItem
                          className="flex-1"
                          value={NoiseReduction.NEAR_FIELD}
                        >
                          <User className="mr-2 h-4 w-4" />
                          {t(
                            "components.advancedSettings.optionNoiseReduction.nearOption"
                          )}
                        </ToggleGroupItem>
                      </ToggleGroup>
                      <TooltipHelp
                        content={t(
                          "components.advancedSettings.optionNoiseReduction.tooltip"
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="w-full">
                  <div className="flex items-center justify-end gap-2">
                    <Label className="text-tinted-grey-3 text-sm">
                      {t(
                        "components.advancedSettings.optionNoiseSuppression.label"
                      )}
                    </Label>
                    <Switch
                      checked={noiseSuppression}
                      onCheckedChange={(checked) => {
                        saveNoiseSuppression(checked);
                      }}
                    />
                    <TooltipHelp
                      content={t(
                        "components.advancedSettings.optionNoiseSuppression.tooltip"
                      )}
                    />
                  </div>
                </div>
                {!isProductionEnv && (
                  <TranscriptionProvidersSelector
                    value={transcriptionProviderValue}
                    values={transcriptionProviderValues}
                    onChange={transcriptionProviderHandleChange}
                  />
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
